/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { refreshUserInfoRequest, watchState } from '../actions';
import Loader from '../common/Loader';
import DefaultLayout from '../layout/DefaultLayout';
import PageTitle from '../components/PageTitle';
import SignIn from '../pages/Authentication/SignIn';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';

import { ToastContainer } from 'react-toastify';
import GetStarted from '../pages/Authentication/GetStarted';
import GetStartedEnterprise from '../pages/Authentication/GetStartedEnterprise';
import GetStartedGitlab from '../pages/Authentication/GetStartedGitlab';
import GetStartedBitbucket from '../pages/Authentication/GetStartedBitbucket';
import ResetPassword from '../pages/Authentication/ResetPassword';
import AcceptInvite from '../pages/Authentication/AcceptInvite';
import SignUpNew from '../pages/Authentication/SignUpNew';
import McpAuthorization from '../pages/Authentication/McpAuthorization';
import CertificationProofPage from '../pages/Public/CertificationProofPage';
import CertificationEmbedPage from '../pages/Public/CertificationEmbedPage';
import CertificationMeaningPage from '../pages/Public/CertificationMeaningPage';
import ChangePassword from './Settings/ChangePassword';
import UnderConstructionPage from './ConstructionPage';
import { getDashboardRoutes, getSettingsRoutes } from '../constants/routes';
import { resolveUserRole } from '../constants/rolePermissions';
import AnalyticsTracker from './Analytics/AnalyticsTracker';
import VisitorTracking from './Tracking/VisitorTracking';
import { INTEGRATION_RETURN_PATH_KEY } from '../utils/integrations';

// Helper component for consistent page layout
const PageLayout = ({
  pageName,
  pageSubtitle = '',
  pageActions = [],
  hidePageHeader = false,
  title,
  children
}) => (
  <div>
    {!hidePageHeader &&
      ((pageName && pageName.trim()) ||
        (pageSubtitle && pageSubtitle.trim())) && (
        <Breadcrumb
          pageName={pageName}
          pageSubtitle={pageSubtitle}
          pageActions={pageActions}
        />
      )}
    <PageTitle title={title} />
    {children}
  </div>
);

// Auth routes configuration
const authRoutes = [
  { path: '/signup', component: SignUpNew },
  { path: '/get-started', component: GetStarted },
  { path: '/get-started-enterprise', component: GetStartedEnterprise },
  { path: '/get-started-gitlab', component: GetStartedGitlab },
  { path: '/get-started-bitbucket', component: GetStartedBitbucket },
  { path: '/reset-password', component: ResetPassword },
  { path: '/accept-invite/:tokenId', component: AcceptInvite },
  { path: '/proof/certifications/:proofId', component: CertificationProofPage },
  {
    path: '/proof/certifications/:proofId/meaning',
    component: CertificationMeaningPage
  },
  { path: '/embed/certifications/:proofId', component: CertificationEmbedPage }
];

// Routes are now dynamically generated from routes.js based on user role

const App = () => {
  const dispatch = useDispatch();
  const { user, loading } = useSelector(state => state);
  const isAuthenticated = Boolean(user);

  useEffect(() => {
    dispatch(watchState());
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      dispatch(refreshUserInfoRequest());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const pendingMcpAuthorization = sessionStorage.getItem(
      'mcpAuthorizationRequest'
    );
    if (
      isAuthenticated &&
      pendingMcpAuthorization &&
      window.location.pathname !== '/mcp/authorize'
    ) {
      window.location.replace(`/mcp/authorize${pendingMcpAuthorization}`);
    }
  }, [isAuthenticated]);

  const resolvePageTitle = route =>
    route.title || route.pageName || route.label;
  const resolvePageName = route => route.pageName || route.label || '';
  const resolvePageSubtitle = route => route.pageSubtitle || '';
  const userRole = resolveUserRole(user?.role, user?.roleName, user?.userRole);
  const mustChooseIntegration =
    user?.passwordChanged === true &&
    (user?.leadType === null ||
      user?.leadType === undefined ||
      !user?.leadType);
  const integrationReturnPath = localStorage.getItem(
    INTEGRATION_RETURN_PATH_KEY
  );
  const isSelfHostedConnectPath = window.location.pathname.startsWith(
    '/settings/integrations/self-hosted/'
  );

  // Authentication routes renderer
  const renderAuthRoutes = () => (
    <>
      {authRoutes.map(route => (
        <Route
          key={route.path}
          path={route.path}
          element={<route.component />}
        />
      ))}
    </>
  );

  // Dashboard routes renderer (requires hasCommits)
  const renderDashboardRoutes = (hasCommits = true) => {
    const dashboardRoutes = getDashboardRoutes(userRole);

    return (
      <>
        {dashboardRoutes.map((route, index) => (
          <Route
            key={route.path}
            index={index === 0}
            path={route.path}
            element={
              <PageLayout
                pageName={resolvePageName(route)}
                pageSubtitle={resolvePageSubtitle(route)}
                title={resolvePageTitle(route)}
                pageActions={route.pageActions || []}
                hidePageHeader={route.hidePageHeader}
              >
                {hasCommits || route.allowWithoutCommits ? (
                  <route.component />
                ) : (
                  <UnderConstructionPage pageTitle={resolvePageTitle(route)} />
                )}
              </PageLayout>
            }
          />
        ))}

        {/* Special routes */}
        <Route
          path="/calendar"
          element={
            <PageLayout pageName="Calendar" title="Calendar">
              {hasCommits ? null : (
                <UnderConstructionPage pageTitle="Calendar" />
              )}
            </PageLayout>
          }
        />
      </>
    );
  };

  // Settings routes renderer (role-based access)
  const renderSettingsRoutes = () => {
    const settingsRoutes = getSettingsRoutes(userRole);

    // Filter out change-password if user hasn't changed password yet
    // (it has a special route handler that takes precedence)
    const filteredRoutes =
      user && user.passwordChanged === false
        ? settingsRoutes.filter(
            route => route.path !== '/settings/change-password'
          )
        : settingsRoutes;

    return (
      <>
        {filteredRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <PageLayout
                pageName={resolvePageName(route)}
                pageSubtitle={resolvePageSubtitle(route)}
                title={resolvePageTitle(route)}
                pageActions={route.pageActions || []}
              >
                <route.component />
              </PageLayout>
            }
          />
        ))}
        <Route
          path="/settings/company"
          element={<Navigate to="/settings/card-management" replace />}
        />
        <Route path="/signIn" element={<SignIn />} />
        <Route path="/login" element={<SignIn />} />
      </>
    );
  };

  const renderRemovedAdminRoutes = () => (
    <>
      {[
        '/support',
        '/support-panel',
        '/collaborations',
        '/collaborations/:programId'
      ].map(path => (
        <Route key={path} path={path} element={<Navigate to="/" replace />} />
      ))}
    </>
  );

  useEffect(() => {
    if (
      integrationReturnPath &&
      user?.passwordChanged === true &&
      !isSelfHostedConnectPath &&
      !mustChooseIntegration
    ) {
      localStorage.removeItem(INTEGRATION_RETURN_PATH_KEY);
      window.location.href = integrationReturnPath;
      return;
    }
  }, [
    integrationReturnPath,
    isSelfHostedConnectPath,
    mustChooseIntegration,
    user?.passwordChanged
  ]);

  return (
    <div>
      <ToastContainer />
      {!loading && (
        <Router>
          <VisitorTracking />
          <AnalyticsTracker />
          <Routes>
            {renderAuthRoutes()}
            <Route path="/mcp/authorize" element={<McpAuthorization />} />

            {user ? (
              user.passwordChanged || user.passwordChanged === undefined ? (
                mustChooseIntegration ? (
                  // Existing lead users without leadType must complete integration selection first
                  <Route
                    path="*"
                    element={
                      <DefaultLayout>
                        <Routes>
                          {renderRemovedAdminRoutes()}
                          {renderSettingsRoutes()}
                          <Route
                            path="*"
                            element={
                              <Navigate to="/settings/integrations" replace />
                            }
                          />
                        </Routes>
                      </DefaultLayout>
                    }
                  />
                ) : user.hasCommits ? (
                  // Has commits - full access to dashboard
                  <Route
                    path="*"
                    element={
                      <DefaultLayout>
                        <Routes>
                          {renderRemovedAdminRoutes()}
                          {renderDashboardRoutes(true)}
                          {renderSettingsRoutes()}
                        </Routes>
                      </DefaultLayout>
                    }
                  />
                ) : (
                  // No commits - dashboard shows under construction, settings accessible
                  <Route
                    path="*"
                    element={
                      <DefaultLayout>
                        <Routes>
                          {renderRemovedAdminRoutes()}
                          {renderDashboardRoutes(false)}
                          {renderSettingsRoutes()}
                        </Routes>
                      </DefaultLayout>
                    }
                  />
                )
              ) : (
                // Password not changed - redirect to change password
                <>
                  <Route
                    path="/settings/change-password"
                    element={
                      <DefaultLayout>
                        <Breadcrumb pageName="Change Password" />
                        <PageTitle title="Change Password" />
                        <ChangePassword />
                      </DefaultLayout>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <Navigate to="/settings/change-password" replace />
                    }
                  />
                </>
              )
            ) : (
              <Route path="*" element={<SignIn />} />
            )}
          </Routes>
        </Router>
      )}

      {loading && <Loader />}
    </div>
  );
};

export default App;
